// Operator dashboard — unified, org-wide.
//
// Two scopes, switched by the tab bar:
//   • "All Hosts" (scope='all')  → org-wide aggregate. KPIs sum across every
//     PBX host, per-host tiles summarize each, and the call/attack tables merge
//     rows from all hosts with a PBX column. Served by /api/v1/dashboard/*
//     (routes/dashboard.js fans out across the org's agents).
//   • A single host (scope=agentId) → that host's own numbers, plus host
//     specifics (memory, load, disk, uptime). Served by the per-agent proxy at
//     /api/v1/agents/:agentId/proxy/dashboard/*.
//
// Selecting a host tab also points the top-bar agent picker at that host so the
// live-call WebSocket (wired in AppShell against the selected agentId) tracks
// the same host you're looking at.
//
// Every fetch is defensive — an unreachable host contributes nothing but never
// errors the whole page.

import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { api } from '../lib/api.js';
import useExtensionLines from '../hooks/useExtensionLines.js';
import {
  callParties, fmtPhone, stateWord, elapsed, collapseCallLegs, lineFor,
  describeDialplanStop,
} from '../lib/calls.js';
import { useAuth } from '../hooks/useAuth.js';
import { useAgentStore } from '../store/agentStore.jsx';

// ---------- Formatting helpers ----------
function fmtBytes(n) {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
}
function fmtUptime(seconds) {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  return `${d}d ${h}h`;
}
function fmtDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
function pct(used, total) {
  if (!total) return null;
  return Math.round((used / total) * 100);
}
// Turn `vmu1000` → `→ Voicemail (ext 1000)`; leave everything else alone.
function fmtEndpoint(v) {
  const s = String(v ?? '');
  const vmu = s.match(/^vmu?(\d+)$/i);
  if (vmu) return `→ Voicemail (ext ${vmu[1]})`;
  if (s === 's') return '→ trunk';
  return s;
}

// Base path for a dashboard sub-resource given the current scope.
//   scope 'all'      → org aggregate route
//   scope <agentId>  → that host's proxy route
function dashPath(scope, sub) {
  return scope === 'all'
    ? `/dashboard/${sub}`
    : `/agents/${scope}/proxy/dashboard/${sub}`;
}

// ---------- Presentational primitives ----------
function KpiCard({ label, value, hint, accent = 'border-blue-500' }) {
  return (
    <div className={`rounded-lg border-t-2 ${accent} bg-white p-4 shadow-sm`}>
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value ?? '—'}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-0.5 text-base font-medium text-gray-900">{value ?? '—'}</div>
    </div>
  );
}

/**
 * Which of the three states a host is in.
 *
 * Exported and pure so the RULE can be tested rather than the markup — the same
 * reasoning as `activeCallCount` below it. This one has been wrong in both
 * directions within a week, so the rule is the thing worth defending:
 *
 *   2026-09-18  a healthy host drawn as dead, for not serving one optional endpoint
 *   2026-09-24  pbx-04 drawn as online for 44 hours while answering 503 to everything
 *
 * The API tells these apart now (`reachable` + `degraded`). This is where the
 * card learns to.
 */
export function hostState(h) {
  if (!h?.reachable) return 'unreachable';
  return h.degraded ? 'degraded' : 'online';
}

/**
 * How many hosts are actually serving.
 *
 * A degraded host does NOT count. "Hosts online 3/3" over a host that can serve
 * nothing is the headline that let the pbx-04 outage run for two days, and a
 * count is the one thing on this page people read without clicking anything.
 */
export function onlineCount(hosts = []) {
  return hosts.filter((h) => hostState(h) === 'online').length;
}

// THREE STATES, NOT TWO. `reachable` alone has now been wrong in both directions
// on this card: on 2026-09-18 it drew a healthy host as dead for not serving one
// optional endpoint, and on 2026-09-24 it drew pbx-04 as green for 44 hours while
// that host answered 503 to every tenant-scoped request. The API learned to tell
// those apart (`degraded`); this did not, so the second one still rendered green.
//
// Amber rather than grey, because the operator action differs: an unreachable
// host needs someone to look at the host, a degraded one is up and answering
// health while failing real reads — and it carries the host's own reason.
function StatusDot({ reachable, degraded }) {
  const state = hostState({ reachable, degraded });
  const tone = { unreachable: 'bg-gray-100 text-gray-500', degraded: 'bg-amber-100 text-amber-800', online: 'bg-green-100 text-green-700' }[state];
  const dot = { unreachable: 'bg-gray-400', degraded: 'bg-amber-500', online: 'bg-green-500' }[state];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {state}
    </span>
  );
}

function KindPill({ kind }) {
  const cls = {
    outbound:  'bg-blue-100 text-blue-700',
    inbound:   'bg-green-100 text-green-700',
    voicemail: 'bg-amber-100 text-amber-700',
    internal:  'bg-gray-100 text-gray-700',
    other:     'bg-gray-100 text-gray-500',
  }[kind] || 'bg-gray-100 text-gray-500';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {kind || 'other'}
    </span>
  );
}

function DispositionPill({ disposition }) {
  const key = String(disposition || '').toUpperCase();
  const cls = {
    ANSWERED: 'bg-green-100 text-green-700',
    FAILED:   'bg-red-100 text-red-700',
    'NO ANSWER': 'bg-amber-100 text-amber-700',
    BUSY:     'bg-amber-100 text-amber-700',
  }[key] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {disposition ? disposition.toLowerCase() : 'unknown'}
    </span>
  );
}

// ---------- Chart cell ----------
// `metric` + `since` params work identically on the aggregate and per-agent
// routes, so the only thing that varies with scope is the base path.
function TimeChart({ scope, title, subtitle, metric, kind = 'line', color = '#4f46e5', sinceOffsetSec = 3600 }) {
  const { data, isError } = useQuery({
    queryKey: ['dashboard', 'timeseries', scope, metric, sinceOffsetSec],
    queryFn: async () => {
      const since = Math.floor(Date.now() / 1000) - sinceOffsetSec;
      return api.get(dashPath(scope, 'timeseries'), { metric, since });
    },
    refetchInterval: 60_000,
    retry: 1,
  });

  const points = useMemo(() => {
    const raw = Array.isArray(data?.points) ? data.points : [];
    return raw.map((p) => ({ ts: p.ts, value: p.value }));
  }, [data]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2">
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </div>
      <div style={{ height: 200 }}>
        {isError || points.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">
            {isError ? 'No data for this metric yet.' : 'Collecting data…'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {kind === 'area' ? (
              <AreaChart data={points} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`fill-${scope}-${metric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="ts" tickFormatter={(t) => new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} width={40} />
                <Tooltip
                  labelFormatter={(t) => new Date(t * 1000).toLocaleString()}
                  formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : v)}
                  contentStyle={{ borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#fill-${scope}-${metric})`} />
              </AreaChart>
            ) : (
              <LineChart data={points} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="ts" tickFormatter={(t) => new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} width={40} />
                <Tooltip
                  labelFormatter={(t) => new Date(t * 1000).toLocaleString()}
                  formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : v)}
                  contentStyle={{ borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ---------- Host tabs ----------
function HostTabs({ hosts, scope, onSelect }) {
  // A degraded host is NOT counted as online. "3/3 online" over a host that can
  // serve nothing is the headline that let pbx-04's outage run for two days.
  const okCount = onlineCount(hosts);
  const degradedCount = hosts.filter((h) => hostState(h) === 'degraded').length;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={() => onSelect('all')}
        className={`rounded-full px-3 py-1 text-sm font-medium ${
          scope === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
        }`}
      >
        All Hosts
        <span className="ml-1.5 text-xs opacity-75">{okCount}/{hosts.length}</span>
        {degradedCount > 0 && (
          <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-xs font-medium text-amber-800">
            {degradedCount} degraded
          </span>
        )}
      </button>
      {hosts.map((h) => (
        <button
          key={h.id}
          onClick={() => onSelect(h.id)}
          title={h.hostname || h.id}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${
            scope === h.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${
            { unreachable: 'bg-gray-400', degraded: 'bg-amber-500', online: 'bg-green-500' }[hostState(h)]
          }`} />
          {h.name || h.id}
        </button>
      ))}
    </div>
  );
}

// ---------- Per-host tiles (aggregate view) ----------
function HostTiles({ hosts, onSelect }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {hosts.map((h) => {
        const s = h.summary;
        const memPct = s?.host?.memory ? pct(s.host.memory.usedBytes, s.host.memory.totalBytes) : null;
        return (
          <button
            key={h.id}
            onClick={() => onSelect(h.id)}
            className="rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:shadow"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">{h.name || h.id}</div>
                <div className="font-mono text-xs text-gray-500">{h.hostname || '—'}</div>
              </div>
              <StatusDot reachable={h.reachable} degraded={h.degraded} />
            </div>
            {s ? (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <MiniStat label="Ext" value={s.extensions ?? '—'} />
                <MiniStat label="Reg" value={
                  s.registeredCount != null && s.endpoints?.total != null
                    ? `${s.registeredCount}/${s.endpoints.total}` : '—'} />
                <MiniStat label="Calls" value={s.channels?.activeCalls ?? '—'} />
                <MiniStat label="Mem" value={memPct != null ? `${memPct}%` : '—'} />
                <MiniStat label="Load" value={s.host?.load ? s.host.load.one.toFixed(2) : '—'} />
                <MiniStat label="Uptime" value={
                  h.health?.uptime_seconds != null ? fmtUptime(h.health.uptime_seconds)
                    : (s.host?.uptime ? fmtUptime(s.host.uptime.seconds) : '—')} />
              </div>
            ) : (
              <div className="mt-3 text-xs text-gray-400">{h.error || 'No data available.'}</div>
            )}
            {/* The host's own words, and the whole point of the degraded state:
                "no Aria tenant is pinned for this agent" is the entire diagnosis,
                and hiding it behind a tile that says online cost two days. */}
            {h.reachable && h.degraded && h.error && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                {h.error}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------- Host specifics strip (scoped view) ----------
// `disk` comes from the host's /health probe (via /dashboard/hosts) — the
// agent exposes disk there and on the heartbeat snapshot, not on the dashboard
// summary. Shape: { totalBytes, usedBytes, availableBytes, usedPct }.
function HostStrip({ summary, disk, stackUptimeSeconds }) {
  const host = summary?.host;
  if (!host) return null;
  const memPct = host.memory ? pct(host.memory.usedBytes, host.memory.totalBytes) : null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <MiniStat label="Hostname" value={<span className="font-mono text-xs">{host.hostname || '—'}</span>} />
        {/* "Uptime" = PBX/agent stack (last PBX restart); "Host uptime" = the VM's OS uptime. */}
        <MiniStat label="Uptime" value={fmtUptime(stackUptimeSeconds ?? host.uptime?.seconds)} />
        <MiniStat label="Host uptime" value={fmtUptime(host.uptime?.seconds)} />
        <MiniStat label="Load average" value={host.load
          ? `${host.load.one.toFixed(2)} · ${host.load.five.toFixed(2)} · ${host.load.fifteen.toFixed(2)}`
          : '—'} />
        <MiniStat label="CPUs" value={host.load?.cpus ?? '—'} />
        <MiniStat label="Memory" value={host.memory
          ? `${memPct}% · ${fmtBytes(host.memory.usedBytes)} / ${fmtBytes(host.memory.totalBytes)}`
          : '—'} />
        <MiniStat label="Disk" value={disk
          ? `${disk.usedPct}% · ${fmtBytes(disk.usedBytes)} / ${fmtBytes(disk.totalBytes)}`
          : '—'} />
      </div>
    </div>
  );
}

// ---------- 24h call-volume line chart ----------
// The agent's timeseries only exposes gauge metrics (concurrent channels, host
// stats) — not a call-count series. So we derive call volume by bucketing the
// recent-calls rows (each carries a `ts`) into hourly counts over the last 24h.
// Pull a generous limit so a busy day isn't truncated; if an org exceeds it the
// earliest hours undercount (flagged in the subtitle).
function CallVolumeChart({ scope }) {
  const LIMIT = 1000;
  const { data, isError } = useQuery({
    queryKey: ['dashboard', 'call-volume', scope],
    queryFn: () => api.get(dashPath(scope, 'recent-calls'), { limit: LIMIT }),
    refetchInterval: 60_000,
    retry: 1,
  });

  const points = useMemo(() => {
    const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
    const hourMs = 3600_000;
    const startHour = Math.floor((Date.now() - 23 * hourMs) / hourMs) * hourMs;
    const buckets = new Map();
    for (let i = 0; i < 24; i++) buckets.set(startHour + i * hourMs, 0);
    for (const r of rows) {
      if (!r.ts) continue;
      const b = Math.floor((r.ts * 1000) / hourMs) * hourMs;
      if (buckets.has(b)) buckets.set(b, buckets.get(b) + 1);
    }
    return [...buckets.entries()].map(([ts, value]) => ({ ts: ts / 1000, value }));
  }, [data]);

  const total = points.reduce((s, p) => s + p.value, 0);
  const capped = total >= LIMIT;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2">
        <div className="text-sm font-semibold text-gray-900">Calls (24h)</div>
        <div className="text-xs text-gray-500">
          {total} call{total === 1 ? '' : 's'} in the last 24 hours, by hour.
          {capped && ' (capped — earliest hours may undercount)'}
        </div>
      </div>
      <div style={{ height: 200 }}>
        {isError ? (
          <div className="flex h-full items-center justify-center text-xs text-gray-400">
            Agent unreachable or no call data yet.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="ts" tickFormatter={(t) => new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit' })} stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} width={40} allowDecimals={false} />
              <Tooltip
                labelFormatter={(t) => new Date(t * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit' })}
                formatter={(v) => [`${v} call${v === 1 ? '' : 's'}`, 'Calls']}
                contentStyle={{ borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12 }}
              />
              <Line type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// The customer's own end of a call, named as the number they dial rather than as the
// extension or ring-group id Asterisk used to get there.
function CallLine({ value, map, legs, dcontext }) {
  // `dst` is sometimes a dialplan label rather than a destination — `i` when the
  // caller pressed an option that does not exist, `s` at the start of a route. Those
  // used to print as a bare letter in this column. There is no line to resolve for
  // them (the customer's end is implied by the context), so say what happened.
  const stop = describeDialplanStop(value, dcontext);
  if (stop) {
    return (
      <>
        <span className="text-xs text-gray-500">—</span>
        <span className="ml-2 text-xs text-gray-500">{stop}</span>
      </>
    );
  }
  const line = lineFor(value, map);
  if (!line) return <span className="font-mono text-xs text-gray-500">{fmtEndpoint(value)}</span>;

  return <LineLabel line={line} legs={legs} />;
}

/**
 * A resolved line, in the Line column of any call table.
 *
 * ONE RENDERER FOR BOTH TABLES. Live Calls had its own copy of this markup, so when
 * Kevin asked on 2026-08-17 to "click on a recent call row and go to the subscriber's
 * detail page", fixing Recent Calls left the identical cell one card above it
 * unlinked. Two copies of a rule is one edit away from disagreeing
 * (feedback_invariants_need_a_function) — so there is now one.
 *
 * THE LINK IS CONDITIONAL. The map carries `subscriberId`, but not always: a line can
 * resolve by the extension convention without being attached to a subscriber. A link
 * to /subscribers/null looks exactly like a working one until it is clicked, so a
 * line without an id stays plain text.
 */
function LineLabel({ line, legs }) {
  const body = (
    <>
      <span className="font-mono text-xs text-gray-900">{fmtPhone(line.number) || line.number}</span>
      {(line.label || line.subscriberName) && (
        <span className="ml-2 text-xs text-gray-500">{line.label || line.subscriberName}</span>
      )}
      {/* Said once, quietly: the merge is not hiding anything, it is un-duplicating. */}
      {legs > 1 && <span className="ml-2 text-[10px] text-gray-400">rang {legs} phones</span>}
    </>
  );
  if (line.subscriberId == null) return body;
  return (
    <Link
      to={`/subscribers/${line.subscriberId}`}
      className="hover:underline"
      title={`Open ${line.subscriberName || 'this customer'}`}
    >
      {body}
    </Link>
  );
}

// ---------- Call tables ----------
// `showHost` adds the PBX column — on only in the aggregate view, where rows
// carry an agentName tag from the merge.
export function RecentCallsCard({ scope, showHost }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', 'recent-calls', scope],
    queryFn:  () => api.get(dashPath(scope, 'recent-calls'), { limit: 15 }),
    refetchInterval: 30_000,
    retry: 1,
  });
  const extLines = useExtensionLines();
  // ONE CALL PER ROW. FreePBX writes a CDR row per ring-group member it dialled — same
  // second, same caller, same destination, different uniqueid — so one unanswered call to
  // Lyons Fire filled four rows (Kevin, 2026-08-13: "one call turns into 4 recent calls").
  // The merge keeps the leg that was ANSWERED; see lib/calls.js.
  const rows = useMemo(
    () => collapseCallLegs(Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : []),
    [data],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-sm font-semibold text-gray-900">Recent Calls</div>
        <div className="text-xs text-gray-500">
          The most recent customer calls{showHost ? ' across all hosts' : ''}, one row per call.
          Excludes rejected SIP scanner traffic — see Attack Attempts below.
        </div>
      </div>
      {isLoading ? (
        <div className="px-4 py-6 text-sm text-gray-400">Loading…</div>
      ) : isError ? (
        <div className="px-4 py-6 text-sm text-gray-400">Agent unreachable or endpoint not available yet.</div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-400">No calls recorded.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">When</th>
                {showHost && <th className="px-4 py-2 text-left font-medium">PBX</th>}
                <th className="px-4 py-2 text-left font-medium">Type</th>
                <th className="px-4 py-2 text-left font-medium">Line</th>
                <th className="px-4 py-2 text-left font-medium">Other party</th>
                <th className="px-4 py-2 text-left font-medium">Duration</th>
                <th className="px-4 py-2 text-left font-medium">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => (
                <tr key={`${r.agentId || scope}-${r.uniqueid}-${r.ts}`}>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                    {new Date(r.ts * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  {showHost && <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600">{r.agentName || r.agentId || '—'}</td>}
                  <td className="px-4 py-2"><KindPill kind={r.kind} /></td>
                  {/* OURS AND THEIRS, not src and dst. On an inbound call `dst` is the ring
                      group (`03038235696`) and on an outbound one it is the number dialled,
                      so a column headed "To" meant something different on alternate rows.
                      `kind` comes from the dialplan context and IS trustworthy, so it
                      decides which side is the customer's. */}
                  <td className="whitespace-nowrap px-4 py-2">
                    <CallLine value={r.kind === 'outbound' ? r.src : r.dst} map={extLines} legs={r.legs} dcontext={r.dcontext} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">
                    {fmtPhone(r.kind === 'outbound' ? r.dst : r.src) || fmtEndpoint(r.kind === 'outbound' ? r.dst : r.src)}
                    {r.cnam && <span className="ml-2 font-sans text-gray-500">{r.cnam}</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{fmtDuration(r.billsec)}</td>
                  <td className="px-4 py-2"><DispositionPill disposition={r.disposition} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AttackAttemptsCard({ scope, showHost }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard', 'recent-attacks', scope],
    queryFn:  () => api.get(dashPath(scope, 'recent-attacks'), { limit: 15 }),
    refetchInterval: 60_000,
    retry: 1,
  });
  const rows = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="text-sm font-semibold text-gray-900">Attack Attempts</div>
        <div className="text-xs text-gray-500">
          Unauthenticated SIP INVITEs rejected with a congestion tone{showHost ? ', across all hosts' : ''}. fail2ban bans repeat offenders automatically.
        </div>
      </div>
      {isLoading ? (
        <div className="px-4 py-6 text-sm text-gray-400">Loading…</div>
      ) : isError ? (
        <div className="px-4 py-6 text-sm text-gray-400">Agent unreachable or endpoint not available yet.</div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-400">Nothing recorded in the recent window.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">When</th>
                {showHost && <th className="px-4 py-2 text-left font-medium">PBX</th>}
                <th className="px-4 py-2 text-left font-medium">Purported source</th>
                <th className="px-4 py-2 text-left font-medium">SIP CLID</th>
                <th className="px-4 py-2 text-left font-medium">Held</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => (
                <tr key={`${r.agentId || scope}-${r.uniqueid}-${r.ts}`}>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                    {new Date(r.ts * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  {showHost && <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600">{r.agentName || r.agentId || '—'}</td>}
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{r.src}</td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-500">{r.clid}</td>
                  <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{fmtDuration(r.duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Live-calls card ----------
/**
 * How many calls are in flight, for the tab being looked at.
 *
 * THE CARD AND THE TABLE HAVE TO AGREE. This lived inline and counted the live WebSocket map
 * only on a HOST tab; on All Hosts it always returned `summary.channels.activeCalls`, a poll.
 * So the Live Calls table could have a row in it while the card above said 0 (Kevin,
 * 2026-08-13). Two sources for one number, and the card preferred the slower one.
 *
 * A row in that table IS a call in flight, on any tab, so the live map wins whenever it has
 * anything. The poll is the fallback for when the socket has told us nothing — which is not
 * the same as "no calls", but it is the only evidence there is.
 *
 * @param liveCalls  the scoped WebSocket entries — the same ones the table renders
 * @param summary    the polled dashboard summary
 */
export function activeCallCount(liveCalls, summary) {
  const live = Array.isArray(liveCalls) ? liveCalls.length : 0;
  if (live > 0) return live;
  return summary?.channels?.activeCalls ?? '—';
}

// ONE ROW PER CALL, IN WORDS AN OPERATOR USES.
//
// What this replaced: State / PBX / Direction / Ext / From / To / Started — six raw fields
// straight off the wire, three of which were wrong for an inbound ring-group call and one
// of which nobody could interpret ("I don't know what Started is" — Kevin, 2026-08-13).
// See lib/calls.js for the capture that settled what each field really means.
function LiveCallRow({ r, map, hostName, tick }) {
  void tick;  // re-render trigger for the elapsed column; the value is not read
  const { line, extension, other } = callParties(r, map);
  const state = stateWord(r.state);
  const talking = state === 'Talking';
  // Every extension this call has touched, not the last leg to arrive.
  const ringing = (r.exts || (extension ? [extension] : [])).length;

  return (
    <tr>
      <td className="px-4 py-2">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          talking ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'
        }`}
        >
          {state}
        </span>
      </td>
      <td className="px-4 py-2 text-xs text-gray-600">{r.agentName || hostName || '—'}</td>
      <td className="whitespace-nowrap px-4 py-2">
        {line ? (
          <LineLabel line={line} />
        ) : (
          // Not resolvable — an extension outside the naming convention, or the lookup has
          // not loaded. Show what the PBX said rather than an empty cell.
          <span className="font-mono text-xs text-gray-500">{extension || r.ext || '—'}</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">
        {other ? fmtPhone(other) : <span className="text-gray-400">not known yet</span>}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600">
        {ringing > 1 ? `${ringing} extensions` : (extension || '—')}
      </td>
      <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-500">
        {elapsed(r.startedAt) || '—'}
      </td>
    </tr>
  );
}

// Consumes the AppShell-provided `activeCalls` map (populated from the WS
// call.state_change stream for the top-bar-selected host).
export function LiveCallsCard({ activeCalls, note, hostName }) {
  const rows = Object.values(activeCalls || {})
    .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
  // Asterisk's vocabulary → the customer's. Null while it loads, and the row falls back to
  // the raw extension rather than waiting.
  const extLines = useExtensionLines();
  // "For" counts up, so the card needs a heartbeat of its own — the WebSocket only speaks
  // when the call's STATE changes, which on a ringing call is not for thirty seconds.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!rows.length) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [rows.length]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Live Calls</div>
          <div className="text-xs text-gray-500">
            {note || 'Real-time call state via WebSocket. Rows disappear on hangup.'}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
          rows.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${rows.length > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          {rows.length} active
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-400">No calls in progress.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">State</th>
                <th className="px-4 py-2 text-left font-medium">PBX</th>
                <th className="px-4 py-2 text-left font-medium">Line</th>
                <th className="px-4 py-2 text-left font-medium">Other party</th>
                <th className="px-4 py-2 text-left font-medium">Ringing</th>
                <th className="px-4 py-2 text-left font-medium">For</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => <LiveCallRow key={r.key} r={r} map={extLines} hostName={hostName} tick={tick} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Main ----------
export default function Overview() {
  const { user, orgName } = useAuth();
  // Only the setter is used: the page shows all hosts and writes the selection
  // for other pages to read. The top-level agent selector went away in #200.
  const { setAgentId } = useAgentStore();
  const { activeCalls = {} } = useOutletContext() || {};

  // scope: 'all' (org aggregate) or an agentId (single host).
  const [scope, setScope] = useState('all');

  // THE LIVE CALLS THIS TAB IS ABOUT — derived once and used by both the KPI card and the
  // table. They used to be worked out separately, which is how the table showed a call while
  // the card said 0. A host tab scopes to that host; All Hosts shows every one.
  const scopedActiveCallsMap = useMemo(() => (
    scope === 'all'
      ? (activeCalls || {})
      : Object.fromEntries(Object.entries(activeCalls || {})
        .filter(([, c]) => !c.agentId || c.agentId === scope))
  ), [activeCalls, scope]);
  const scopedActiveCalls = Object.values(scopedActiveCallsMap);

  // Host list + live probe drives the tab bar and the aggregate tiles.
  const { data: hostsData } = useQuery({
    queryKey: ['dashboard', 'hosts'],
    queryFn:  () => api.get('/dashboard/hosts'),
    refetchInterval: 30_000,
    retry: 1,
  });
  const hosts = Array.isArray(hostsData?.hosts) ? hostsData.hosts : [];

  // Selecting a host tab points the top-bar picker at it so the live-call WS
  // follows. 'all' leaves the picker alone.
  function selectScope(next) {
    setScope(next);
    if (next !== 'all') setAgentId(next);
  }

  // If the currently-scoped host disappears from the list, fall back to all.
  useEffect(() => {
    if (scope !== 'all' && hosts.length > 0 && !hosts.some((h) => h.id === scope)) {
      setScope('all');
    }
  }, [scope, hosts]);

  const isAll = scope === 'all';

  const { data: summary, isError: summaryError } = useQuery({
    queryKey: ['dashboard', 'summary', scope],
    queryFn:  () => api.get(dashPath(scope, 'summary')),
    refetchInterval: 30_000,
    retry: 1,
  });

  // The feed is org-wide now (OrgRelay fans in every AgentRelay), so the aggregate
  // view really does show every host. It previously carried only the top-bar host's
  // calls and said so — naming a picker that had already been removed, which is how
  // a real call on PBX-02 read as "0 active".
  const liveNote = isAll
    ? 'Real-time call state across every PBX host. Rows disappear on hangup.'
    : 'Real-time call state on this host. Rows disappear on hangup.';

  const noHosts = hostsData && hosts.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Welcome, {user?.name || 'operator'}</h1>
        {orgName && <p className="mt-1 text-sm text-gray-500">{orgName}</p>}
      </div>

      {noHosts && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
          <h2 className="text-sm font-semibold text-blue-900">No PBX hosts yet</h2>
          <p className="mt-2 text-sm text-blue-800">
            Head to{' '}
            <Link to="/settings?tab=pbx-hosts" className="font-medium underline">Settings → PBX Hosts</Link>{' '}
            to add one.
          </p>
        </div>
      )}

      {hosts.length > 0 && (
        <>
          <HostTabs hosts={hosts} scope={scope} onSelect={selectScope} />

          {summaryError && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Couldn&rsquo;t reach {isAll ? 'one or more agents' : 'the agent'}. Data will populate once reachable.
            </div>
          )}

          {/* KPI row */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label={isAll ? 'Extensions (all hosts)' : 'Extensions'}
              value={summary?.extensions ?? '—'}
              hint={<Link to="/subscribers" className="underline">manage subscribers</Link>}
              accent="border-blue-500"
            />
            <KpiCard
              label="Registered endpoints"
              value={
                summary?.registeredCount != null && summary?.endpoints?.total != null
                  ? `${summary.registeredCount} / ${summary.endpoints.total}`
                  : '—'
              }
              hint={summary?.endpoints ? `${summary.endpoints.unavailable} unavailable` : null}
              accent="border-emerald-500"
            />
            <KpiCard
              label="Active calls"
              // THE SAME ROWS THE TABLE BELOW IS SHOWING.
              //
              // This counted the live WebSocket map only on a HOST tab, and on All Hosts
              // always fell back to `summary.channels.activeCalls` — a poll. So the Live
              // Calls table could have a call in it while this card read 0 (Kevin,
              // 2026-08-13). Two sources for one number, and the card preferred the one
              // that lags.
              //
              // The live map wins wherever it has anything, on any tab, because a row in
              // that table IS a call in flight. The poll is the fallback for when the
              // socket has told us nothing — which is not the same as "no calls", but it
              // is the only evidence there is.
              value={activeCallCount(scopedActiveCalls, summary)}
              hint={summary?.channels
                ? `${summary.channels.activeChannels} channel${summary.channels.activeChannels === 1 ? '' : 's'}`
                : null}
              accent="border-cyan-500"
            />
            {isAll ? (
              <KpiCard
                label="Hosts online"
                value={summary?.hostsIncluded != null && summary?.hostsTotal != null
                  ? `${summary.hostsIncluded} / ${summary.hostsTotal}`
                  : '—'}
                hint="click a host tab for details"
                accent="border-amber-500"
              />
            ) : (
              <KpiCard
                label="Host memory"
                value={summary?.host?.memory
                  ? `${pct(summary.host.memory.usedBytes, summary.host.memory.totalBytes)}%`
                  : '—'}
                hint={summary?.host?.memory
                  ? `${fmtBytes(summary.host.memory.usedBytes)} / ${fmtBytes(summary.host.memory.totalBytes)}`
                  : null}
                accent="border-amber-500"
              />
            )}
          </div>

          {/* Aggregate: per-host tiles. Scoped: host specifics strip. */}
          {isAll
            ? <HostTiles hosts={hosts} onSelect={selectScope} />
            : <HostStrip summary={summary} disk={hosts.find((h) => h.id === scope)?.health?.disk} stackUptimeSeconds={hosts.find((h) => h.id === scope)?.health?.uptime_seconds} />}

          {/* Calls (24h) — full width */}
          <CallVolumeChart scope={scope} />

          {/* Charts */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TimeChart scope={scope} title="Active channels"      subtitle="Last hour" metric="channels.active"      kind="area" color="#06b6d4" />
            <TimeChart scope={scope} title="Registered endpoints" subtitle="Last hour" metric="endpoints.registered" kind="line" color="#10b981" />
            {!isAll && <TimeChart scope={scope} title="Host memory used %"   subtitle="Last hour" metric="host.memory.used_pct" kind="area" color="#f59e0b" />}
            {!isAll && <TimeChart scope={scope} title="Load average (1-min)" subtitle="Last hour" metric="host.load.one"        kind="line" color="#4f46e5" />}
          </div>

          <LiveCallsCard
            activeCalls={scopedActiveCallsMap}
            note={liveNote}
            hostName={isAll ? null : (hosts.find((h) => (h.agentId || h.agent_id || h.id) === scope)?.name || null)}
          />
          <RecentCallsCard    scope={scope} showHost={isAll} />
          <AttackAttemptsCard scope={scope} showHost={isAll} />
        </>
      )}
    </div>
  );
}
